import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import {
  byDueDate,
  INSTALLMENT_STATE_COLORS,
  InstallmentState,
  inState,
  installmentState,
  stateCounts,
} from '../../utils/installmentStatus';

export interface ListedInstallment {
  id: string;
  status: string;
  amount: number;
  paidAmount: number;
  balanceAmount: number;
  dueDate: string;
}

interface InstallmentListProps<T extends ListedInstallment> {
  installments: T[];
  nameOf: (inst: T) => string;
  formatMoney: (amount: number) => string;
  formatDate: (iso: string) => string;
  onPress: (inst: T) => void;
  /** Paid and partly paid instalments offer their receipt. */
  onReceipt: (inst: T) => void;
}

/**
 * Every instalment of the year on the "All installments" sheet (app and web): a count of
 * each status on top, then each instalment by due date with its status, total, paid,
 * balance and due date, and the receipt where something was paid. The counts are filters:
 * the sheet opens on every instalment, tapping a status shows just those, and tapping it
 * again (or "All") shows every one.
 */
export function InstallmentList<T extends ListedInstallment>({
  installments, nameOf, formatMoney, formatDate, onPress, onReceipt,
}: InstallmentListProps<T>) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const sorted = useMemo(() => byDueDate(installments), [installments]);
  const counts = useMemo(() => stateCounts(installments), [installments]);
  const [picked, setPicked] = useState<InstallmentState | null>(null);
  // A status that empties out (the last overdue one just paid) falls back to every instalment.
  const shown = picked && counts.some(c => c.state === picked) ? picked : null;
  const listed = useMemo(() => inState(sorted, shown), [sorted, shown]);

  return (
    <View testID="fee-all-installments">
      <View style={styles.countRow} accessibilityRole="tablist">
        <TouchableOpacity
          style={[styles.countChip, styles.allChip, !shown && styles.allChipOn]}
          onPress={() => setPicked(null)}
          accessibilityRole="tab"
          accessibilityState={{ selected: !shown }}
          testID="fee-installment-filter-All"
        >
          <Text style={[styles.countText, styles.allText, !shown && styles.chipTextOn]}>{`All ${installments.length}`}</Text>
        </TouchableOpacity>
        {counts.map(({ state, count }) => {
          const c = INSTALLMENT_STATE_COLORS[state];
          const on = shown === state;
          return (
            <TouchableOpacity
              key={state}
              // The picked status fills with its colour; the rest stay tinted.
              style={[styles.countChip, { backgroundColor: on ? c.text : c.bg, borderColor: on ? c.text : c.bg }]}
              onPress={() => setPicked(on ? null : state)}
              accessibilityRole="tab"
              accessibilityState={{ selected: on }}
              accessibilityHint={on ? 'Shows every instalment' : `Shows only ${state.toLowerCase()} instalments`}
              testID={`fee-installment-filter-${state}`}
            >
              <Text style={[styles.countText, { color: c.text }, on && styles.chipTextOn]}>{`${state} ${count}`}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {listed.map(inst => {
        const state = installmentState(inst);
        const c = INSTALLMENT_STATE_COLORS[state];
        const somethingPaid = inst.paidAmount > 0 || state === 'Paid';
        return (
          <TouchableOpacity key={inst.id} style={[styles.card, { borderLeftColor: c.text }]} onPress={() => onPress(inst)}
            testID={`fee-installment-${inst.id}`}>
            <View style={styles.head}>
              <Text style={styles.name} numberOfLines={2}>{nameOf(inst)}</Text>
              <View style={[styles.badge, { backgroundColor: c.bg }]}>
                <Text style={[styles.badgeText, { color: c.text }]}>{state}</Text>
              </View>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>Total amount</Text>
              <Text style={styles.value}>{formatMoney(inst.amount)}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Paid</Text>
              <Text style={[styles.value, inst.paidAmount > 0 && { color: '#15803D' }]}>{formatMoney(inst.paidAmount)}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Balance</Text>
              <Text style={[styles.value, inst.balanceAmount > 0 && { color: '#B91C1C' }]}>{formatMoney(inst.balanceAmount)}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Due date</Text>
              <Text style={styles.value}>{inst.dueDate ? formatDate(inst.dueDate) : '—'}</Text>
            </View>

            {somethingPaid && (
              <TouchableOpacity style={styles.receiptBtn} onPress={() => onReceipt(inst)} accessibilityRole="button"
                testID={`fee-installment-receipt-${inst.id}`}>
                <FeatherIcon name="file-text" size={14} color={colors.primary} />
                <Text style={styles.receiptText}>View receipt</Text>
              </TouchableOpacity>
            )}
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  countRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 14 },
  // Tall enough to tap (each chip is a filter), with a border so the picked one keeps its size.
  countChip: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 999, borderWidth: 1 },
  countText: { fontSize: 12, fontWeight: '800' },
  allChip: { backgroundColor: colors.card, borderColor: colors.border },
  allChipOn: { backgroundColor: colors.primary, borderColor: colors.primary },
  allText: { color: colors.textSecondary },
  chipTextOn: { color: '#FFFFFF' },
  card: {
    backgroundColor: colors.card,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.border,
    borderLeftWidth: 4,
    padding: 14,
    marginBottom: 12,
    gap: 6,
  },
  head: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginBottom: 4 },
  name: { flex: 1, fontSize: 15, fontWeight: '800', color: colors.textPrimary },
  badge: { paddingHorizontal: 10, paddingVertical: 3, borderRadius: 999 },
  badgeText: { fontSize: 11.5, fontWeight: '800' },
  row: { flexDirection: 'row', justifyContent: 'space-between' },
  label: { fontSize: 13, color: colors.textSecondary },
  value: { fontSize: 13, fontWeight: '700', color: colors.textPrimary },
  receiptBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start', marginTop: 6 },
  receiptText: { fontSize: 13, fontWeight: '700', color: colors.primary },
});
